import dayjs, { Dayjs } from "utils/dayjs"

import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import { Guid, IApiLink } from "@/types"

export enum AssetMaintenanceStatus {
    overdue = "overdue",
    pending = "pending",
    scheduled = "scheduled",
    completed = "completed",
}

export interface IGetAssetMaintenantcesParams {
    pageNumber?: number
    pageSize?: number
    includeCompleted?: boolean
    statuses?: AssetMaintenanceStatus[]
    maintenanceTypeIds?: Guid[]
    profitCenterIds?: (Guid | null)[]
    assetId?: Guid
}

export interface IGetAssetMaintenanceDto {
    id: Guid
    assetId: Guid
    articleId: Guid
    typeId: Guid
    status: AssetMaintenanceStatus
    thresholdOperationHours?: number | null
    thresholdDate?: string | null
    remainingOperationHours?: number | null
    remainingDays?: number | null
    completedDate?: string | null
    completedDocumentUri?: string | null
    completedComment?: string | null
    links?: IApiLink[]
}

export interface ICreateUpdateAssetMaintenanceDto {
    typeId: Guid
    thresholdOperationHours?: number | null
    thresholdDate?: string | null
}

export interface ICreateUpdateAssetMaintenance extends Omit<ICreateUpdateAssetMaintenanceDto, "thresholdDate"> {
    thresholdDate?: Dayjs | null
}

export interface ICompleteAssetMaintenanceDto {
    date: string
    comment?: string | null
    fileId?: string | null
    followUpMaintenance?: ICreateUpdateAssetMaintenanceDto | null
}

export interface ICompleteAssetMaintenance extends Omit<ICompleteAssetMaintenanceDto, "date" | "followUpMaintenance"> {
    date: Dayjs
    followUpMaintenance?: ICreateUpdateAssetMaintenance | null
}

export default class AssetMaintenance {
    id: Guid
    assetId: Guid
    articleId: Guid
    typeId: Guid
    status: AssetMaintenanceStatus
    thresholdOperationHours?: number
    thresholdDate?: Dayjs
    remainingOperationHours?: number
    remainingDays?: number
    completedDate?: Dayjs
    completedDocumentUri?: string
    completedComment?: string

    linkUpdate?: IApiLink
    linkDelete?: IApiLink
    linkComplete?: IApiLink

    constructor(dto: IGetAssetMaintenanceDto) {
        this.id = dto.id
        this.assetId = dto.assetId
        this.articleId = dto.articleId
        this.typeId = dto.typeId
        this.status = dto.status
        this.thresholdOperationHours = dto.thresholdOperationHours ?? undefined
        this.thresholdDate = dto.thresholdDate ? dayjs.utc(dto.thresholdDate, "YYYY-MM-DD") : undefined
        this.remainingOperationHours = dto.remainingOperationHours ?? undefined
        this.remainingDays = dto.remainingDays ?? undefined
        this.completedDate = dto.completedDate ? dayjs.utc(dto.completedDate, "YYYY-MM-DD") : undefined
        this.completedDocumentUri = dto.completedDocumentUri ?? undefined
        this.completedComment = dto.completedComment ?? undefined

        this.linkUpdate = dto.links?.find(l => l.entity === ApiLinkEntity.assetMaintenance && l.rel === ApiLinkRel.update)
        this.linkDelete = dto.links?.find(l => l.entity === ApiLinkEntity.assetMaintenance && l.rel === ApiLinkRel.delete)
        this.linkComplete = dto.links?.find(l => l.entity === ApiLinkEntity.assetMaintenanceComplete && l.rel === ApiLinkRel.update)
    }

    static getCreateUpdateDto(data: ICreateUpdateAssetMaintenance): ICreateUpdateAssetMaintenanceDto {
        return {
            ...data,
            thresholdDate: data.thresholdDate?.format("YYYY-MM-DD")
        }
    }

    static getCompleteDto(data: ICompleteAssetMaintenance): ICompleteAssetMaintenanceDto {
        return {
            ...data,
            date: data.date.format("YYYY-MM-DD"),
            followUpMaintenance: data.followUpMaintenance ? AssetMaintenance.getCreateUpdateDto(data.followUpMaintenance) : undefined,
        }
    }

    get daysDiff(): number | null {
        return this.thresholdDate
            ? this.thresholdDate.diff(dayjs.utc().startOf('day'), 'days')
            : null
    }

    getOperationHoursDiff(assetOperationHours: number | null): number | null {
        return this.thresholdOperationHours && assetOperationHours
            ? this.thresholdOperationHours - assetOperationHours
            : null
    }
}

